import React, { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router'
import { useDispatch } from 'react-redux'
import {
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Divider,
  Typography,

} from '@material-ui/core'
import moment from 'moment'
import localization from 'moment/locale/pt-br'

import AdjustIcon from '@material-ui/icons/Adjust'
import BusinessIcon from '@material-ui/icons/Business'
import PersonIcon from '@material-ui/icons/Person'
import ScheduleIcon from '@material-ui/icons/Schedule'

import { setIdCaso } from 'actions'

import { useCadaverService } from 'hooks/useCadaverService'
import { useVestigioService } from 'hooks/useVestigioService'
import useRouter from 'utils/useRouter'
import {
  getLabelClassificacaoCadaver,
  getLabelMetodoIdentificacaoCadaver,
  getLabelGrauRelacaoCadaver
} from 'helpers/labels'

import { Page, Header, ObjetoAvatar, SecaoGenerica } from 'components'
import { ExibirArquivo } from 'components/ExibirArquivo'
import { CardExameSolicitado } from 'components/Cards/CardExameSolicitado'
import { CardCaso } from 'components/Cards/CardCaso'
import { CardVestigio } from 'components/Cards/CardVestigio'
import { ListaInformacoes } from 'views/Informacao/ListaInformacoes'
import { HistoricoOperacoes } from 'components/HistoricoOperacoes'

import { MenuAcoes } from './components/MenuAcoes'
import { useStyles } from './Cadaver.styles'

export const Cadaver = (props) => {
  const { route } = props
  moment.locale('pt-br', localization)
  const classes = useStyles()
  const params = useParams()
  const router = useRouter()
  const dispatch = useDispatch()

  const cadaverService = useCadaverService()
  const vestigioService = useVestigioService()


  const [cadaver, setCadaver] = useState(null)
  const [carregando, setCarregando] = useState(false)
  const [vestigiosExtraidos, setVestigiosExtraidos] = useState(null)

  const fetchDados = useCallback(async () => {
    setCarregando(true)
    const [dadosCadaver, extraidos] = await Promise.all([
      cadaverService.listarCadaverAsync(params.id),
      vestigioService.listarVestigiosExtraidosDeCadaverAsync(params.id),
    ])
    setCadaver(dadosCadaver)
    setVestigiosExtraidos(extraidos)
    setCarregando(false)
  }, [params.id])

  useEffect(() => {
    if (params.id) {
      setCadaver(null)
      setVestigiosExtraidos(null)
      fetchDados()
    }
  }, [fetchDados, params.id])

  const handleClickExameSolicitado = (id) => {
    router.history.push(`/casos/caso/solicitacao/exameSolicitado/detalhar/${id}`)
  }

  const handleClickCaso = (id, novaAba) => {
    dispatch(setIdCaso(id))
    if (novaAba) {
      window.open(`/casos/caso/detalhar/${id}`, '_blank', 'noreferrer')
    } else {
      router.history.push(`/casos/caso/detalhar/${id}`)
    }
  }

  const handleClickVestigio = (id, novaTab) => {
    if (novaTab) {
      window.open(`/casos/caso/vestigio/detalhar/${id}`, '_blank', 'noreferrer')
    } else {
      router.history.push(`/casos/caso/vestigio/detalhar/${id}`)
    }
  }

  return (
    <Page className={classes.root} title={`${route.secao} - SIGEP`}>
      <Header titulo={`${route.secao}`} overline={route.secao} />

      <div className={classes.resultsWrapper}>
        {!cadaver ? (
          <div className={classes.carregando}>
            <CircularProgress disableShrink />
          </div>
        ) : (
          <>

            <Card className={classes.headerCard}>
              <CardContent style={{ padding: '24px' }}>
                <Grid container spacing={3} alignItems="center" justifyContent="space-between">
                  <Grid item xs={12} sm={6} md={7} style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <ObjetoAvatar tipo="CADAVER" />
                    <Typography variant="h3" className={classes.caseTitle}>
                      {cadaver.nic ? `NIC: ${cadaver.nic.toUpperCase()}` : 'SEM NIC'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={5} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {!carregando && (
                      <MenuAcoes cadaver={cadaver} setCadaver={setCadaver} />
                    )}
                  </Grid>
                </Grid>

                <Divider style={{ margin: '20px 0 24px 0' }} />

                <Typography variant="subtitle1" className={classes.caseSubtitle} style={{ marginTop: 0, marginBottom: '24px' }}>
                  {cadaver.descricaoGeral?.toUpperCase() || 'SEM DESCRIÇÃO ADICIONADA'}
                </Typography>

                <Grid container spacing={3} className={classes.metaGrid} alignItems="stretch">
                  <Grid item xs={12} sm={6} md={3}>
                    <div className={classes.metaItem}>
                      <AdjustIcon className={classes.metaIcon} />
                      <div className={classes.metaTextContainer}>
                        <Typography className={classes.metaLabel}>STATUS</Typography>
                        <Typography className={classes.metaValue}>
                          {cadaver.status?.toUpperCase() || '-'}
                        </Typography>
                      </div>
                    </div>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <div className={classes.metaItem}>
                      <PersonIcon className={classes.metaIcon} />
                      <div className={classes.metaTextContainer}>
                        <Typography className={classes.metaLabel}>CLASSIFICAÇÃO</Typography>
                        <Typography className={classes.metaValue}>
                          {getLabelClassificacaoCadaver(cadaver.classificacao)?.toUpperCase() || '-'}
                        </Typography>
                      </div>
                    </div>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <div className={classes.metaItem}>
                      <BusinessIcon className={classes.metaIcon} />
                      <div className={classes.metaTextContainer}>
                        <Typography className={classes.metaLabel}>NECROTÉRIO</Typography>
                        <Typography className={classes.metaValue}>
                          {cadaver.necroterio
                            ? `${cadaver.necroterio.nome?.toUpperCase()} - ${cadaver.necroterio.divisao?.sigla?.toUpperCase()}`
                            : '-'}
                        </Typography>
                      </div>
                    </div>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <div className={classes.metaItem}>
                      <ScheduleIcon className={classes.metaIcon} />
                      <div className={classes.metaTextContainer}>
                        <Typography className={classes.metaLabel}>DATA DE ENTRADA</Typography>
                        <Typography className={classes.metaValue}>
                          {moment(cadaver.createdAt).format('DD/MM/YYYY HH:mm')}
                        </Typography>
                      </div>
                    </div>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>


            <SecaoGenerica titulo="Dados de Entrada e Coleta">
              <Grid container spacing={3}>
                <Grid item md={4} sm={6} xs={12}>
                  <Typography className={classes.metaLabel}>CAUSA DA MORTE (PRELIMINAR)</Typography>
                  <Typography className={classes.metaValue}>
                    {cadaver.causaJuridicaMortePreliminar?.toUpperCase() || 'NÃO INFORMADA'}
                  </Typography>
                </Grid>

                <Grid item md={4} sm={6} xs={12}>
                  <Typography className={classes.metaLabel}>CAUSA MÉDICA DA MORTE</Typography>
                  <Typography className={classes.metaValue}>
                    {cadaver.causMort?.nome?.toUpperCase() === 'OUTRO'
                      ? cadaver.causaMorteTextoLivre?.toUpperCase()
                      : cadaver.causMort?.nome?.toUpperCase() || 'NÃO INFORMADA'}
                  </Typography>
                </Grid>

                <Grid item md={4} sm={6} xs={12}>
                  <Typography className={classes.metaLabel}>RESPONSÁVEL PELA COLETA</Typography>
                  <Typography className={classes.metaValue}>
                    {cadaver?.usuCol?.nome?.toUpperCase() === 'OUTRO'
                      ? cadaver?.usuarioColetaInput?.toUpperCase() || 'NÃO INFORMADO'
                      : cadaver.usuCol?.nome?.toUpperCase() || '-'}
                  </Typography>
                </Grid>

                <Grid item md={4} sm={6} xs={12}>
                  <Typography className={classes.metaLabel}>RESPONSÁVEL PELO TRANSPORTE</Typography>
                  <Typography className={classes.metaValue}>
                    {cadaver?.usuTrans?.nome?.toUpperCase() === 'OUTRO'
                      ? cadaver?.usuarioTransporteInput?.toUpperCase() || 'NÃO INFORMADO'
                      : cadaver.usuTrans?.nome?.toUpperCase() || '-'}
                  </Typography>
                </Grid>

                <Grid item md={8} sm={6} xs={12}>
                  <Typography className={classes.metaLabel}>ENDEREÇO DE ORIGEM</Typography>
                  <Typography className={classes.metaValue}>
                    {cadaver.endOrig
                      ? `${cadaver.endOrig.rua?.toUpperCase() || ''}, ${cadaver.endOrig.numero?.toUpperCase() || ''} ${cadaver.endOrig.bairro?.toUpperCase() || ''}. ${cadaver.endOrig.cidade?.toUpperCase() || ''}, ${cadaver.endOrig.estado?.toUpperCase() || ''}`
                      : '-'}
                  </Typography>
                  {cadaver.endOrig && (
                    <Typography variant="caption" color="textSecondary" style={{ marginTop: '4px', display: 'block' }}>
                      {`Latitude: ${cadaver.endOrig.latitudeGps || '-'}. Longitude: ${cadaver.endOrig.longitudeGps || '-'}`}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </SecaoGenerica>


            {cadaver.inumacoes && cadaver.inumacoes.length > 0 && (
              <SecaoGenerica titulo="Dados da Inumação">
                {cadaver.inumacoes.map((inumacao, index) => (
                  <div key={inumacao.id} className={classes.containerDadoEntrega}>
                    <Grid container spacing={3}>
                      <Grid item md={4} sm={6} xs={12}>
                        <Typography className={classes.metaLabel}>DATA DO ÓBITO</Typography>
                        <Typography className={classes.metaValue}>
                          {inumacao.dataObito ? moment(inumacao.dataObito).format('DD/MM/YYYY') : '-'}
                        </Typography>
                      </Grid>

                      <Grid item md={4} sm={6} xs={12}>
                        <Typography className={classes.metaLabel}>DATA DA INUMAÇÃO</Typography>
                        <Typography className={classes.metaValue}>
                          {inumacao.dataInumacao ? moment(inumacao.dataInumacao).format('DD/MM/YYYY') : '-'}
                        </Typography>
                      </Grid>

                      <Grid item md={4} sm={6} xs={12}>
                        <Typography className={classes.metaLabel}>CEMITÉRIO</Typography>
                        <Typography className={classes.metaValue}>
                          {inumacao.cemiterio?.toUpperCase() || '-'}
                        </Typography>
                      </Grid>

                      <Grid item md={4} sm={6} xs={12}>
                        <Typography className={classes.metaLabel}>LOCALIZAÇÃO INTERNA NO CEMITÉRIO</Typography>
                        <Typography className={classes.metaValue}>
                          {inumacao.localizacao_interna?.toUpperCase() || '-'}
                        </Typography>
                      </Grid>

                      <Grid item md={4} sm={6} xs={12}>
                        <Typography className={classes.metaLabel}>ADMINISTRADOR DO CEMITÉRIO</Typography>
                        <Typography className={classes.metaValue}>
                          {inumacao.cemiterioAdm?.toUpperCase() || '-'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                          {inumacao.cemiterioAdmTipoDoc && `${inumacao.cemiterioAdmTipoDoc?.toUpperCase()}: ${inumacao.cemiterioAdmDoc || '-'}`}
                          {inumacao.cemiterioAdmOrgaoExpedidor && ` (${inumacao.cemiterioAdmOrgaoExpedidor?.toUpperCase()})`}
                        </Typography>
                      </Grid>

                      <Grid item md={4} sm={6} xs={12}>
                        <Typography className={classes.metaLabel}>AGENTES RESPONSÁVEIS</Typography>
                        <Typography className={classes.metaValue}>
                          {inumacao.agenteA ? `${inumacao.agenteA.nome?.toUpperCase()} - ${inumacao.agenteA.matricula || ''}` : ''}
                        </Typography>
                        <Typography className={classes.metaValue}>
                          {inumacao.agenteB ? `${inumacao.agenteB.nome?.toUpperCase()} - ${inumacao.agenteB.matricula || ''}` : ''}
                        </Typography>
                      </Grid>

                      <Grid item md={4} sm={6} xs={12}>
                        <Typography className={classes.metaLabel}>TESTEMUNHA 1</Typography>
                        <Typography className={classes.metaValue}>
                          {inumacao.testemunhaANome?.toUpperCase() || '-'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                          {inumacao.testemunhaATipoDoc && `${inumacao.testemunhaATipoDoc?.toUpperCase()}: ${inumacao.testemunhaADoc || '-'}`}
                          {inumacao.testemunhaAOrgaoExpedidor && ` (${inumacao.testemunhaAOrgaoExpedidor?.toUpperCase()})`}
                        </Typography>
                      </Grid>

                      <Grid item md={4} sm={6} xs={12}>
                        <Typography className={classes.metaLabel}>TESTEMUNHA 2</Typography>
                        <Typography className={classes.metaValue}>
                          {inumacao.testemunhaBNome?.toUpperCase() || '-'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                          {inumacao.testemunhaBTipoDoc && `${inumacao.testemunhaBTipoDoc?.toUpperCase()}: ${inumacao.testemunhaBDoc || '-'}`}
                          {inumacao.testemunhaBOrgaoExpedidor && ` (${inumacao.testemunhaBOrgaoExpedidor?.toUpperCase()})`}
                        </Typography>
                      </Grid>
                    </Grid>
                    {index < cadaver.inumacoes.length - 1 && <Divider style={{ margin: '24px 0' }} />}
                  </div>
                ))}
              </SecaoGenerica>
            )}


            {cadaver.exumacoes && cadaver.exumacoes.length > 0 && (
              <SecaoGenerica titulo="Dados da Exumação">
                {cadaver.exumacoes.map((exumacao, index) => (
                  <div key={exumacao.id} className={classes.containerDadoEntrega}>
                    <Grid container spacing={3}>
                      <Grid item md={4} sm={6} xs={12}>
                        <Typography className={classes.metaLabel}>DATA DA EXUMAÇÃO</Typography>
                        <Typography className={classes.metaValue}>
                          {exumacao.dataExumacao ? moment(exumacao.dataExumacao).format('DD/MM/YYYY') : '-'}
                        </Typography>
                      </Grid>

                      <Grid item md={4} sm={6} xs={12}>
                        <Typography className={classes.metaLabel}>TIPO DA EXUMAÇÃO</Typography>
                        <Typography className={classes.metaValue}>
                          {exumacao.tipoExumacao?.toUpperCase() || '-'}
                        </Typography>
                      </Grid>

                      <Grid item md={4} sm={6} xs={12}>
                        <Typography className={classes.metaLabel}>SOLICITANTE</Typography>
                        <Typography className={classes.metaValue}>
                          {exumacao.solicitanteNome?.toUpperCase() || '-'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                          {exumacao.solicitanteTipoDoc && `${exumacao.solicitanteTipoDoc?.toUpperCase()}: ${exumacao.solicitanteDoc || '-'}`}
                          {exumacao.solicitanteOrgaoExpedidor && ` (${exumacao.solicitanteOrgaoExpedidor?.toUpperCase()})`}
                        </Typography>
                      </Grid>

                      <Grid item md={4} sm={6} xs={12}>
                        <Typography className={classes.metaLabel}>AGENTES RESPONSÁVEIS</Typography>
                        <Typography className={classes.metaValue}>
                          {exumacao.agenteA ? `${exumacao.agenteA.nome?.toUpperCase()} - ${exumacao.agenteA.matricula || ''}` : ''}
                        </Typography>
                        <Typography className={classes.metaValue}>
                          {exumacao.agenteB ? `${exumacao.agenteB.nome?.toUpperCase()} - ${exumacao.agenteB.matricula || ''}` : ''}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography className={classes.metaLabel}>MOTIVO</Typography>
                        <Typography className={classes.metaValue}>
                          {exumacao.motivo?.toUpperCase() || '-'}
                        </Typography>
                      </Grid>
                    </Grid>
                    {index < cadaver.exumacoes.length - 1 && <Divider style={{ margin: '24px 0' }} />}
                  </div>
                ))}
              </SecaoGenerica>
            )}


            <SecaoGenerica titulo="Dados de Identificação">
              <Grid container spacing={3}>
                <Grid item md={4} sm={6} xs={12}>
                  <Typography className={classes.metaLabel}>CLASSIFICAÇÃO</Typography>
                  <Typography className={classes.metaValue}>
                    {getLabelClassificacaoCadaver(cadaver.classificacao) || '-'}
                  </Typography>
                </Grid>

                {cadaver.declaracaoObito && (
                  <Grid item md={4} sm={6} xs={12}>
                    <Typography className={classes.metaLabel}>DECLARAÇÃO DE ÓBITO</Typography>
                    <Typography className={classes.metaValue}>
                      {cadaver.declaracaoObito.toUpperCase()}
                    </Typography>
                  </Grid>
                )}

                {(cadaver.classificacao === 'IDENTIFICADO' || cadaver.classificacao === 'RECONHECIDO' || cadaver.classificacao === 'NECROPAPILOSCOPICO_POSITIVO') && (
                  <>
                    {cadaver.classificacao !== 'NECROPAPILOSCOPICO_POSITIVO' && (
                      <Grid item md={4} sm={6} xs={12}>
                        <Typography className={classes.metaLabel}>MÉTODO DE IDENTIFICAÇÃO</Typography>
                        <Typography className={classes.metaValue}>
                          {getLabelMetodoIdentificacaoCadaver(cadaver.metodoIdentificacao) || '-'}
                        </Typography>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Typography className={classes.metaLabel}>NOME COMPLETO</Typography>
                      <Typography className={classes.metaValue}>
                        {cadaver.nome?.toUpperCase() || '-'}
                      </Typography>
                    </Grid>

                    <Grid item md={4} sm={6} xs={12}>
                      <Typography className={classes.metaLabel}>SEXO</Typography>
                      <Typography className={classes.metaValue}>
                        {cadaver.sexo?.toUpperCase() || '-'}
                      </Typography>
                    </Grid>

                    <Grid item md={4} sm={6} xs={12}>
                      <Typography className={classes.metaLabel}>FILIAÇÕES</Typography>
                      <Typography className={classes.metaValue}>
                        {cadaver.filiacoes?.map((fil, idx) => (
                          <div key={idx}>{fil.toUpperCase()}</div>
                        )) || '-'}
                      </Typography>
                    </Grid>

                    {(cadaver.classificacao === 'IDENTIFICADO' || cadaver.classificacao === 'NECROPAPILOSCOPICO_POSITIVO') && cadaver.numeroDocumento && (
                      <Grid item md={4} sm={6} xs={12}>
                        <Typography className={classes.metaLabel}>DOCUMENTO</Typography>
                        <Typography className={classes.metaValue}>
                          {`${cadaver.tipoDocumento?.toUpperCase() || ''} ${cadaver.numeroDocumento?.toUpperCase()} ${cadaver.orgaoEmissorDocumento?.toUpperCase() || ''}`}
                        </Typography>
                      </Grid>
                    )}

                    <Grid item md={4} sm={6} xs={12}>
                      <Typography className={classes.metaLabel}>NATURALIDADE</Typography>
                      <Typography className={classes.metaValue}>
                        {cadaver.naturalidade?.toUpperCase() || '-'}
                      </Typography>
                    </Grid>

                    <Grid item md={4} sm={6} xs={12}>
                      <Typography className={classes.metaLabel}>DATA DE NASCIMENTO</Typography>
                      <Typography className={classes.metaValue}>
                        {cadaver.dataNascimento
                          ? moment(cadaver.dataNascimento).format('DD/MM/YYYY').toUpperCase()
                          : 'NÃO INFORMADA'}
                      </Typography>
                    </Grid>
                  </>
                )}

                <Grid item md={4} sm={6} xs={12}>
                  <Typography className={classes.metaLabel}>ENDEREÇO</Typography>
                  <Typography className={classes.metaValue}>
                    {cadaver.endereco?.toUpperCase() || 'NÃO INFORMADO'}
                  </Typography>
                </Grid>

                <Grid item md={4} sm={6} xs={12}>
                  <Typography className={classes.metaLabel}>ESCOLARIDADE</Typography>
                  <Typography className={classes.metaValue}>
                    {cadaver.escolaridade?.toUpperCase() || 'NÃO INFORMADO'}
                  </Typography>
                </Grid>

                <Grid item md={4} sm={6} xs={12}>
                  <Typography className={classes.metaLabel}>ESTADO CIVIL</Typography>
                  <Typography className={classes.metaValue}>
                    {cadaver.estadoCivil?.toUpperCase() || 'NÃO INFORMADO'}
                  </Typography>
                </Grid>

                <Grid item md={4} sm={6} xs={12}>
                  <Typography className={classes.metaLabel}>ETNIA</Typography>
                  <Typography className={classes.metaValue}>
                    {cadaver.etnia?.toUpperCase() || 'NÃO INFORMADO'}
                  </Typography>
                </Grid>

                <Grid item md={4} sm={6} xs={12}>
                  <Typography className={classes.metaLabel}>PROFISSÃO</Typography>
                  <Typography className={classes.metaValue}>
                    {cadaver.profissao?.toUpperCase() || 'NÃO INFORMADO'}
                  </Typography>
                </Grid>
              </Grid>
            </SecaoGenerica>

            {cadaver.classificacao === 'RECONHECIDO' && (
              <SecaoGenerica titulo="Dados do Responsável pelo Reconhecimento">
                <Grid container spacing={3}>
                  <Grid item md={4} sm={6} xs={12}>
                    <Typography className={classes.metaLabel}>NOME</Typography>
                    <Typography className={classes.metaValue}>
                      {cadaver.reconhec?.nome?.toUpperCase() || '-'}
                    </Typography>
                  </Grid>

                  <Grid item md={4} sm={6} xs={12}>
                    <Typography className={classes.metaLabel}>DOCUMENTO</Typography>
                    <Typography className={classes.metaValue}>
                      {cadaver.reconhec
                        ? `${cadaver.reconhec.tipoDocumento?.toUpperCase() || ''} ${cadaver.reconhec.numeroDocumento?.toUpperCase() || ''} ${cadaver.reconhec.orgaoEmissorDocumento?.toUpperCase() || ''}`
                        : '-'}
                    </Typography>
                  </Grid>

                  <Grid item md={4} sm={6} xs={12}>
                    <Typography className={classes.metaLabel}>GRAU DE RELAÇÃO</Typography>
                    <Typography className={classes.metaValue}>
                      {cadaver.reconhec ? getLabelGrauRelacaoCadaver(cadaver.reconhec.grauRelacao) : '-'}
                    </Typography>
                  </Grid>

                  <Grid item md={4} sm={6} xs={12}>
                    <Typography className={classes.metaLabel}>TELEFONE</Typography>
                    <Typography className={classes.metaValue}>
                      {cadaver.reconhec?.telefone?.toUpperCase() || '-'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography className={classes.metaLabel} style={{ marginBottom: '8px' }}>DOCUMENTOS RELACIONADOS</Typography>
                    {cadaver.reconhec?.arqs && cadaver.reconhec.arqs.length > 0 ? (
                      <Grid container spacing={2}>
                        {cadaver.reconhec.arqs.map((arquivo) => (
                          <Grid item md={2} sm={4} xs={6} key={arquivo.id} className={classes.item}>
                            <ExibirArquivo id={arquivo.id} arquivo={arquivo} rotaLoadFile="/arquivo/reconhecedorcadaver" />
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Typography variant="body2" color="textSecondary">Nenhum documento relacionado</Typography>
                    )}
                  </Grid>
                </Grid>
              </SecaoGenerica>
            )}

            {cadaver.status === 'ENTREGUE' && (
              <>
                <SecaoGenerica titulo="Dados do Responsável pelo Recebimento">
                  <Grid container spacing={3}>
                    <Grid item md={4} sm={6} xs={12}>
                      <Typography className={classes.metaLabel}>NOME</Typography>
                      <Typography className={classes.metaValue}>
                        {cadaver.recebedor?.nome?.toUpperCase() || '-'}
                      </Typography>
                    </Grid>

                    <Grid item md={4} sm={6} xs={12}>
                      <Typography className={classes.metaLabel}>DOCUMENTO</Typography>
                      <Typography className={classes.metaValue}>
                        {cadaver.recebedor
                          ? `${cadaver.recebedor.tipoDocumento?.toUpperCase() || ''} ${cadaver.recebedor.numeroDocumento?.toUpperCase() || ''} ${cadaver.recebedor.orgaoEmissorDocumento?.toUpperCase() || ''}`
                          : '-'}
                      </Typography>
                    </Grid>

                    <Grid item md={4} sm={6} xs={12}>
                      <Typography className={classes.metaLabel}>GRAU DE RELAÇÃO</Typography>
                      <Typography className={classes.metaValue}>
                        {cadaver.recebedor ? getLabelGrauRelacaoCadaver(cadaver.recebedor.grauRelacao) : '-'}
                      </Typography>
                    </Grid>

                    <Grid item md={4} sm={6} xs={12}>
                      <Typography className={classes.metaLabel}>TELEFONE</Typography>
                      <Typography className={classes.metaValue}>
                        {cadaver.recebedor?.telefone?.toUpperCase() || '-'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography className={classes.metaLabel} style={{ marginBottom: '8px' }}>DOCUMENTOS RELACIONADOS</Typography>
                      {cadaver.recebedor?.arqs && cadaver.recebedor.arqs.length > 0 ? (
                        <Grid container spacing={2}>
                          {cadaver.recebedor.arqs.map((arquivo) => (
                            <Grid item md={2} sm={4} xs={6} key={arquivo.id} className={classes.item}>
                              <ExibirArquivo id={arquivo.id} arquivo={arquivo} rotaLoadFile="/arquivo/recebedorcadaver" />
                            </Grid>
                          ))}
                        </Grid>
                      ) : (
                        <Typography variant="body2" color="textSecondary">Nenhum documento relacionado</Typography>
                      )}
                    </Grid>
                  </Grid>
                </SecaoGenerica>

                <SecaoGenerica titulo="Cemitério de Destino">
                  <Grid container spacing={3}>
                    <Grid item md={6} sm={6} xs={12}>
                      <Typography className={classes.metaLabel}>CEMITÉRIO</Typography>
                      <Typography className={classes.metaValue}>
                        {cadaver.cemiterio?.toUpperCase() || '-'}
                      </Typography>
                    </Grid>

                    <Grid item md={6} sm={6} xs={12}>
                      <Typography className={classes.metaLabel}>LOCALIZAÇÃO INTERNA DO CEMITÉRIO</Typography>
                      <Typography className={classes.metaValue}>
                        {cadaver.localizacaoInternaCemiterio?.toUpperCase() || '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                </SecaoGenerica>
              </>
            )}


            <SecaoGenerica titulo="Caso Relacionado">
              {cadaver.caso ? (
                <Grid container spacing={2}>
                  <Grid item md={3} sm={6} xs={12}>
                    <CardCaso caso={cadaver.caso} handleClick={handleClickCaso} />
                  </Grid>
                </Grid>
              ) : (
                <Grid container justifyContent="center" alignItems="center" style={{ padding: '30px 0' }}>
                  <Typography variant="body2" color="textSecondary">Nenhum caso associado</Typography>
                </Grid>
              )}
            </SecaoGenerica>


            <SecaoGenerica titulo="Exames Solicitados">
              {cadaver.exSols && cadaver.exSols.length ? (
                <Grid container spacing={2}>
                  {cadaver.exSols.map((exameSolicitado) => (
                    <Grid item md={3} sm={6} xs={12} key={exameSolicitado.id}>
                      <CardExameSolicitado exameSolicitado={exameSolicitado} handleClick={handleClickExameSolicitado} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Grid container justifyContent="center" alignItems="center" style={{ padding: '30px 0' }}>
                  <Typography variant="body2" color="textSecondary">Nenhum exame solicitado</Typography>
                </Grid>
              )}
            </SecaoGenerica>


            <SecaoGenerica titulo="Vestígios Extraídos">
              {vestigiosExtraidos && vestigiosExtraidos.length ? (
                <Grid container spacing={2}>
                  {vestigiosExtraidos.map((vest) => (
                    <Grid item md={3} sm={6} xs={12} key={vest.id}>
                      <CardVestigio vestigio={vest} handleClick={handleClickVestigio} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Grid container justifyContent="center" alignItems="center" style={{ padding: '30px 0' }}>
                  <Typography variant="body2" color="textSecondary">Nenhum outro vestígio foi extraído deste</Typography>
                </Grid>
              )}
            </SecaoGenerica>


            <SecaoGenerica titulo="Arquivos Relacionados">
              {cadaver.arqs && cadaver.arqs.length ? (
                <Grid container spacing={2}>
                  {cadaver.arqs.map((arquivo) => (
                    <Grid item md={2} sm={4} xs={6} key={arquivo.id} className={classes.item}>
                      <ExibirArquivo id={arquivo.id} arquivo={arquivo} rotaLoadFile="/arquivo/cadaver" />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Grid container justifyContent="center" alignItems="center" style={{ padding: '30px 0' }}>
                  <Typography variant="body2" color="textSecondary">Nenhum arquivo relacionado</Typography>
                </Grid>
              )}
            </SecaoGenerica>


            <SecaoGenerica titulo="Informações Adicionadas">
              <Grid item xs={12}>
                <ListaInformacoes tipo="cadaver" id={cadaver.id} />
              </Grid>
            </SecaoGenerica>


            <SecaoGenerica titulo="Histórico de Operações">
              <Grid item xs={12} style={{ paddingTop: '10px' }}>
                {cadaver.id && <HistoricoOperacoes id={cadaver.id} tipo="CADAVER" refresh={cadaver.status} />}
              </Grid>
            </SecaoGenerica>
          </>
        )}
      </div>
    </Page>
  )
}
